import {
  Button,
  Stack,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Iconify, Label } from '@/components';
import { fDateTime } from '@/utils/formatTime';
import { PermissionsObject } from '@/types';
import { ScheduledJob } from '../types/scheduled-jobs';

type ScheduledJobsTableRowProps = {
  row: ScheduledJob;
  permissions: PermissionsObject;
  onSelectJob: VoidFunction;
  onEditJob: VoidFunction;
  onRunJob: VoidFunction;
  onViewRuns: VoidFunction;
  onDeleteJob: VoidFunction;
  onToggleEnabled: (enabled: boolean) => void;
  toggleDisabled?: boolean;
};

const getStatusColor = (status?: string | null) => {
  const normalized = status?.toLowerCase() ?? '';

  if (['success', 'succeeded', 'completed', 'ok'].includes(normalized)) {
    return 'success' as const;
  }

  if (['failed', 'failure', 'error'].includes(normalized)) {
    return 'error' as const;
  }

  if (['running', 'pending', 'started'].includes(normalized)) {
    return 'warning' as const;
  }

  return 'default' as const;
};

const truncate = (value?: string | null, maxLength: number = 80) => {
  if (!value) return '-';
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

export default function ScheduledJobsTableRow({
  row,
  permissions,
  onSelectJob,
  onEditJob,
  onRunJob,
  onViewRuns,
  onDeleteJob,
  onToggleEnabled,
  toggleDisabled,
}: ScheduledJobsTableRowProps) {
  const theme = useTheme();
  const statusColor = getStatusColor(row.lastStatus);

  return (
    <TableRow hover onClick={onSelectJob} sx={{ cursor: 'pointer' }}>
      <TableCell>
        <Stack spacing={0.25} alignItems="flex-start">
          <Typography
            variant="subtitle2"
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {row.name || 'Untitled job'}
          </Typography>
          {row.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ maxWidth: 280 }}
              noWrap
            >
              {row.description}
            </Typography>
          )}
          {row.lockKey && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.lockKey}
            </Typography>
          )}
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            flexWrap="wrap"
            sx={{ pt: 1 }}
            onClick={(event) => event.stopPropagation()}
          >
            <Button
              size="small"
              variant="outlined"
              disabled={!permissions.canExecute}
              onClick={onRunJob}
              startIcon={<Iconify icon="eva:play-circle-fill" />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Run now
            </Button>

            <Button
              size="small"
              variant="text"
              disabled={!permissions.canUpdate}
              onClick={onEditJob}
              startIcon={<Iconify icon="eva:edit-fill" />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Edit
            </Button>

            <Button
              size="small"
              variant="text"
              onClick={onViewRuns}
              startIcon={<Iconify icon="material-symbols:history" />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Runs
            </Button>

            <Button
              size="small"
              variant="text"
              color="error"
              disabled={!permissions.canDelete}
              onClick={onDeleteJob}
              startIcon={<Iconify icon="eva:trash-2-outline" />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </TableCell>

      <TableCell align="left" onClick={(event) => event.stopPropagation()}>
        <Switch
          checked={row.enabled}
          disabled={!permissions.canUpdate || toggleDisabled}
          onChange={(event) => onToggleEnabled(event.target.checked)}
        />
      </TableCell>

      <TableCell align="left">
        <Typography variant="body2" noWrap>
          {row.cronExpression || '-'}
        </Typography>
      </TableCell>

      <TableCell align="left">{row.timeZone || '-'}</TableCell>

      <TableCell align="left">
        <Label
          variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
          color={row.executionType === 'HTTP' ? 'info' : 'secondary'}
        >
          {row.executionType}
        </Label>
      </TableCell>

      <TableCell align="left">
        <Label color={statusColor}>{row.lastStatus || 'No runs'}</Label>
      </TableCell>

      <TableCell align="left">
        {row.lastRunAt ? fDateTime(row.lastRunAt) : '-'}
      </TableCell>

      <TableCell align="left" sx={{ maxWidth: 240 }}>
        {row.lastError ? (
          <Tooltip title={row.lastError}>
            <Typography variant="body2" noWrap>
              {truncate(row.lastError)}
            </Typography>
          </Tooltip>
        ) : (
          '-'
        )}
      </TableCell>

    </TableRow>
  );
}
